// Minimal i18n: a t(key, params) helper backed by per-locale JSON dictionaries
// loaded eagerly via Vite's JSON imports. Locale is persisted in localStorage
// and falls back to navigator.language; subscribers are notified on change.

import en from '../i18n/en.json';
import hu from '../i18n/hu.json';

const DICTIONARIES = { en, hu };
const FALLBACK = 'en';
const STORAGE_KEY = 'nestly.locale';

const LOCALE_TAGS = { en: 'en-US', hu: 'hu-HU' };
const LISTENERS = new Set();

let current = detectInitialLocale();

function detectInitialLocale() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && DICTIONARIES[stored]) return stored;
  } catch (_) {}
  const nav = (typeof navigator !== 'undefined' && navigator.language) || '';
  const code = nav.toLowerCase().slice(0, 2);
  return DICTIONARIES[code] ? code : FALLBACK;
}

export function availableLocales() {
  return Object.keys(DICTIONARIES);
}

export function getLocale() {
  return current;
}

export function getLocaleTag() {
  return LOCALE_TAGS[current] || current;
}

export function setLocale(code) {
  if (!DICTIONARIES[code] || code === current) return;
  current = code;
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
  LISTENERS.forEach((fn) => { try { fn(code); } catch (_) {} });
}

export function subscribe(fn) {
  LISTENERS.add(fn);
  return () => LISTENERS.delete(fn);
}

// t('a.b.c') -> looks up nested keys; missing keys fall back to `en`, then to
// the key itself. {placeholders} in the resolved string are substituted with
// values from `params` after escaping is the caller's responsibility.
export function t(key, params) {
  const value = lookup(DICTIONARIES[current], key)
    ?? lookup(DICTIONARIES[FALLBACK], key)
    ?? key;
  if (params && typeof value === 'string') return interpolate(value, params);
  return value;
}

function lookup(dict, key) {
  if (!dict) return null;
  const parts = key.split('.');
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return null;
  }
  return typeof cur === 'string' ? cur : null;
}

function interpolate(template, params) {
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, name) =>
    params[name] !== undefined && params[name] !== null ? String(params[name]) : ''
  );
}
